import { useRef, KeyboardEvent, forwardRef, ForwardedRef } from 'react';
import { Flexbox } from '@morressier/ts';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { monthsList, monthIndexMapper } from './utils';
import { InputContainer, Input, Placeholder, CalenderButton } from './style';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IDatepickerInputProps {
  openPicker: () => void;
  selectedDate?: { day?: number; month?: number; year?: number };
  onDateChange: (date: { day?: number; month?: number; year?: number }) => void;
  placeholder?: string;
  type: 'date' | 'month';
  disabled?: boolean;
  error?: string;
}

const DatepickerInput = (
  props: IDatepickerInputProps,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const {
    openPicker,
    selectedDate,
    onDateChange,
    placeholder,
    type,
    disabled,
    error
  } = props;

  const onKeyPressCalender = (e: KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'Enter') {
      openPicker();
    }
  };

  const onKeyPressDay = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      yearRef.current?.focus();
    } else if (e.key === 'ArrowLeft') {
      monthRef.current?.focus();
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      dayRef.current?.focus();
    }
  };

  const onKeyPressMonth = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      selectedDate?.day ? dayRef.current?.focus : yearRef.current?.focus();
    } else if (e.key === 'ArrowLeft') {
      yearRef.current?.focus();
    } else if (e.key === 'ArrowUp' && selectedDate?.month !== undefined) {
      onDateChange({ month: monthIndexMapper(selectedDate?.month + 1) });
    } else if (e.key === 'ArrowDown' && selectedDate?.month !== undefined) {
      onDateChange({ month: monthIndexMapper(selectedDate?.month - 1) });
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      monthRef.current?.focus();
    }
  };

  const onKeyPressYear = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      monthRef.current?.focus();
    } else if (e.key === 'ArrowLeft') {
      selectedDate?.day ? dayRef.current?.focus : monthRef.current?.focus();
    } else if (e.key === 'ArrowUp' && selectedDate?.year) {
      onDateChange({ year: selectedDate?.year + 1 });
    } else if (e.key === 'ArrowDown' && selectedDate?.year) {
      onDateChange({ year: selectedDate?.year - 1 });
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      yearRef.current?.focus();
    }
  };

  const onClickCalender = () => {
    if (disabled) return;
    openPicker();
  };

  return (
    <InputContainer
      alignItems="center"
      justifyContent="space-between"
      ref={ref}
      error={error}
      disabled={disabled}
    >
      {selectedDate?.day || selectedDate?.month || selectedDate?.year ? (
        <Flexbox>
          {selectedDate?.month !== undefined && (
            <Input
              ref={monthRef}
              width={35}
              value={monthsList[selectedDate.month]}
              readOnly
              onKeyDown={onKeyPressMonth}
              disabled={disabled}
            />
          )}
          {selectedDate.day && type === 'date' && (
            <Input
              ref={dayRef}
              value={selectedDate.day}
              readOnly
              onKeyDown={onKeyPressDay}
              disabled={disabled}
            />
          )}
          <Input
            ref={yearRef}
            value={selectedDate.year}
            readOnly
            onKeyDown={onKeyPressYear}
            width={43}
            disabled={disabled}
          />
        </Flexbox>
      ) : (
        <Placeholder>{placeholder}</Placeholder>
      )}
      <CalenderButton
        onClick={onClickCalender}
        onKeyPress={onKeyPressCalender}
        disabled={disabled}
        type="button"
      >
        <FontAwesomeIcon icon={faCalendar} />
      </CalenderButton>
    </InputContainer>
  );
};

export default forwardRef(DatepickerInput);
